import React, { useRef } from 'react';
import {
  AttentionMessage,
  Box,
  Container,
  DataTable,
  Flex,
  Grid,
  Spinner,
  TableState,
} from '@tq/ui-components';
import { useTranslation } from 'react-i18next';
import { useRecoilValue, useSetRecoilState } from 'recoil';

import { useAuditRequests } from 'api';
import { UseTrafficMonitorState } from 'store';
import { CallDirection, ReportData } from 'types';
import { DataTablePagination, EmptyDataCard } from '../../../components';
import { useTrafficMonitorColumns, useTrafficMonitorFilters } from '../hooks';
import { MonitorFilters } from './MonitorFilters';

type MonitorTableProps = {
  callDirection: CallDirection;
};

export const MonitorTable: React.FC<MonitorTableProps> = ({ callDirection }) => {
  const { t } = useTranslation('traffic_monitor');

  const { isLoading } = useAuditRequests(callDirection);
  const {
    pageNumberState,
    pageSizeState,
    sortByState,
    orderByColumnState,
    orderByDescendingState,
    resultsDataState,
    paginationState,
  } = UseTrafficMonitorState(callDirection);
  const filters = useTrafficMonitorFilters(callDirection);
  const columns = useTrafficMonitorColumns();
  const tableRef = useRef(null);
  const sortBy = useRecoilValue(sortByState);
  const results = useRecoilValue(resultsDataState);
  const paginationInfo = useRecoilValue(paginationState);
  const setOrderByColumn = useSetRecoilState(orderByColumnState);
  const setOrderByDescending = useSetRecoilState(orderByDescendingState);

  const handleStateChange = (state: TableState<ReportData>) => {
    const newOrderByColumn = state.sortBy?.[0]?.id;
    const newOrderByDescending = state.sortBy?.[0]?.desc ?? false;

    if (newOrderByColumn) {
      setOrderByColumn(newOrderByColumn);
      setOrderByDescending(newOrderByDescending);
    }
  };

  return (
    <>
      <Container px={{ base: 0, lg: 4, xl: 0 }} mb={6}>
        <Grid gridGap={4}>
          <Box>
            <Grid gridGap={4}>
              <MonitorFilters filters={filters} direction={callDirection} />
            </Grid>
          </Box>
          {callDirection === 'Inbound' && (
            <AttentionMessage
              my={2}
              status='info'
              title={t(
                'http_inbound_code_message',
                'HTTP 403 and HTTP 404 codes cannot be captured and displayed'
              )}
              variant='inline'
            />
          )}
          {isLoading ? (
            <Flex justifyContent='center'>
              <Spinner />
            </Flex>
          ) : !results?.length ? (
            <EmptyDataCard message={t('no_data', 'No results found')} />
          ) : (
            <>
              <Box ref={tableRef} overflow='hidden'>
                <DataTable
                  columns={columns}
                  data={results}
                  disableSortRemove
                  disablePagination
                  enableOverflowScrollingContainer
                  onStateChange={handleStateChange}
                  size='sm'
                  sortBy={sortBy}
                  sx={{
                    '.tqc-datatable__th-content': {
                      minWidth: '130px',
                    },
                  }}
                  whiteSpace='normal'
                />
              </Box>
              <Box marginBottom={24}>
                <DataTablePagination
                  paginationInfo={paginationInfo}
                  pageNumberState={pageNumberState}
                  pageSizeState={pageSizeState}
                  tableRef={tableRef}
                  tableScrollHeaderOffset={167}
                />
              </Box>
            </>
          )}
        </Grid>
      </Container>
    </>
  );
};
